/**
 * O que a Comunicação precisa além da caixa de entrada — trilha T5a.
 *
 * Os sinais do filtro inteligente, as etiquetas da conversa e a transferência
 * de fila. Ficam fora do `caixaDeEntrada` de propósito: a caixa já é uma leitura
 * pesada, e o filtro inteligente é uma pergunta diferente — "o que esta pessoa
 * tem na agenda, no funil e nas propostas".
 */

import { supabase } from '@/supabase';
import { atual } from '@/session/sessao';
import type { PortaDeDados } from './porta';
import type { SinaisDaConversa } from './comunicacao';

const FUSO = 'America/Sao_Paulo';

function cliente() {
    if (!supabase) throw new Error('Servidor não configurado.');
    return supabase;
}

function clinicaId(): string {
    const id = atual()?.clinicaId;
    if (!id) throw new Error('Sessão sem clínica: entre novamente.');
    return id;
}

function erro(mensagem: string, e: { message: string } | null): void {
    if (e) throw new Error(`${mensagem}: ${e.message}`);
}

function linhas(data: unknown): Record<string, unknown>[] {
    return (data ?? []) as Record<string, unknown>[];
}

function nome(v: unknown): string | undefined {
    const x = Array.isArray(v) ? v[0] : v;
    return x && typeof x === 'object' ? ((x as { nome?: string }).nome ?? undefined) : undefined;
}

function diaDe(instante: string | Date): string {
    return new Date(instante).toLocaleDateString('en-CA', { timeZone: FUSO });
}

export type MetodosDaComunicacao = Pick<PortaDeDados, 'sinaisDaCaixa' | 'etiquetarConversa' | 'desetiquetarConversa'
    | 'etiquetarContato' | 'desetiquetarContato' | 'transferirConversa'>;

export const comunicacaoNoSupabase: MetodosDaComunicacao = {
    async sinaisDaCaixa(): Promise<Record<string, SinaisDaConversa>> {
        const [conversasResp, marcadasResp, etiquetasResp] = await Promise.all([
            cliente().from('conversas').select('id, contato_id, fila_id'),
            cliente().from('conversa_etiquetas').select('conversa_id, etiqueta_id, origem'),
            cliente().from('etiquetas').select('id, nome, cor'),
        ]);
        erro('Falha ao ler as conversas', conversasResp.error);
        erro('Falha ao ler as etiquetas das conversas', marcadasResp.error);
        erro('Falha ao ler as etiquetas', etiquetasResp.error);

        const daCaixa = linhas(conversasResp.data);
        const contatos = [...new Set(daCaixa.map((c) => c.contato_id).filter((x): x is string => typeof x === 'string'))];

        /* Três leituras pelos contatos da caixa — nunca a agenda inteira. */
        let agenda: Record<string, unknown>[] = [];
        let leads: Record<string, unknown>[] = [];
        let propostas: Record<string, unknown>[] = [];
        if (contatos.length) {
            const [agendaResp, leadsResp, propostasResp] = await Promise.all([
                cliente().from('agendamentos')
                    .select('id, contato_id, inicio_em, procedimentos(nome), profissional:membros!profissional_id(nome)')
                    .in('contato_id', contatos)
                    .not('status', 'in', '("cancelado","bloqueio")')
                    .order('inicio_em'),
                cliente().from('leads').select('contato_id, etapa').in('contato_id', contatos),
                cliente().from('orcamentos').select('contato_id').in('contato_id', contatos).eq('status', 'enviado'),
            ]);
            erro('Falha ao ler a agenda dos contatos', agendaResp.error);
            erro('Falha ao ler o funil dos contatos', leadsResp.error);
            erro('Falha ao ler as propostas dos contatos', propostasResp.error);
            agenda = linhas(agendaResp.data);
            leads = linhas(leadsResp.data);
            propostas = linhas(propostasResp.data);
        }

        const etiquetas = new Map(linhas(etiquetasResp.data).map((e) => [String(e.id), { nome: String(e.nome ?? ''), cor: String(e.cor ?? '#2986FF') }]));
        const marcadas = linhas(marcadasResp.data);
        const hoje = diaDe(new Date());
        const agora = Date.now();

        const resultado: Record<string, SinaisDaConversa> = {};
        for (const c of daCaixa) {
            const id = String(c.id);
            const contato = typeof c.contato_id === 'string' ? c.contato_id : undefined;
            const daPessoa = contato ? agenda.filter((a) => a.contato_id === contato) : [];
            const proximo = daPessoa.find((a) => new Date(String(a.inicio_em)).getTime() >= agora);
            const deHoje = daPessoa.find((a) => diaDe(String(a.inicio_em)) === hoje);

            resultado[id] = {
                conversaId: id,
                ...(c.fila_id ? { filaId: String(c.fila_id) } : {}),
                etiquetas: marcadas.filter((m) => m.conversa_id === id).flatMap((m) => {
                    const e = etiquetas.get(String(m.etiqueta_id));
                    return e ? [{ id: String(m.etiqueta_id), nome: e.nome, cor: e.cor, origem: m.origem === 'gatilho' ? 'gatilho' as const : 'manual' as const }] : [];
                }),
                procedimentos: [...new Set(daPessoa.map((a) => nome(a.procedimentos)).filter((p): p is string => Boolean(p)))],
                ...(proximo ? {
                    proximoAgendamento: {
                        inicioEm: String(proximo.inicio_em),
                        procedimento: nome(proximo.procedimentos) ?? '',
                        profissional: nome(proximo.profissional) ?? '',
                    },
                } : {}),
                /* `at-<agendamento>` é o id que o editor aceita para a consulta
                   que ainda não foi aberta — o mesmo que a lista de Consultas usa. */
                ...(deHoje ? { atendimentoDeHojeId: `at-${String(deHoje.id)}` } : {}),
                temOportunidade: Boolean(contato) && leads.some((l) => l.contato_id === contato && l.etapa !== 'ganho' && l.etapa !== 'perdido'),
                temProposta: Boolean(contato) && propostas.some((p) => p.contato_id === contato),
            };
        }
        return resultado;
    },

    async etiquetarConversa(conversaId: string, etiquetaId: string): Promise<void> {
        const { error } = await cliente().from('conversa_etiquetas')
            .insert({ clinica_id: clinicaId(), conversa_id: conversaId, etiqueta_id: etiquetaId, origem: 'manual' });
        /* Já etiquetada é o resultado pedido, não erro. */
        if (error?.code === '23505') return;
        if (error?.code === '42501') throw new Error('Etiquetar conversa exige a permissão de comunicação.');
        erro('Falha ao etiquetar a conversa', error);
    },

    async desetiquetarConversa(conversaId: string, etiquetaId: string): Promise<void> {
        const { error } = await cliente().from('conversa_etiquetas')
            .delete().eq('conversa_id', conversaId).eq('etiqueta_id', etiquetaId);
        erro('Falha ao tirar a etiqueta', error);
    },

    /* A mesma dupla, na pessoa (0168). O `clinica_id` vai no corpo porque a chave
       estrangeira composta da tabela exige que ele bata com o do contato: mandar o da
       clínica errada é recusado pelo banco, não só pela RLS. */
    async etiquetarContato(contatoId: string, etiquetaId: string): Promise<void> {
        const { error } = await cliente().from('contato_etiquetas')
            .insert({ clinica_id: clinicaId(), contato_id: contatoId, etiqueta_id: etiquetaId, origem: 'manual' });
        /* Já etiquetada é o resultado pedido, não erro. */
        if (error?.code === '23505') return;
        erro('Falha ao etiquetar a pessoa', error);
    },

    async desetiquetarContato(contatoId: string, etiquetaId: string): Promise<void> {
        const { error } = await cliente().from('contato_etiquetas')
            .delete().eq('contato_id', contatoId).eq('etiqueta_id', etiquetaId);
        erro('Falha ao tirar a etiqueta', error);
    },

    async transferirConversa(conversaId: string, filaId: string, atendenteId?: string): Promise<void> {
        const { error } = await cliente().rpc('transferir_conversa', {
            p_conversa: conversaId, p_fila: filaId, p_atendente: atendenteId ?? null,
        });
        erro('Falha ao transferir a conversa', error);
    },
};
